import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'

/**
 * Determines what parties are allowed to edit the document.
 * Owner = just the owner of the document
 * Clients = any client on the job
 * Everyone = any client and provider on the job
 */
export type DocumentEditPermission = 'Owner' | 'Clients' | 'Everyone' | null

/**
 * Base properties for document access.
 */
export type DocumentAccessProps = ModelProps<DocumentAccess> & {
  accessType: DocumentAccessType
}

/**
 * Properties for job document access.
 */
export interface JobDocumentAccessProps {
  type: 'Job'
  jobId: Id
  editPermission: DocumentEditPermission
}

/**
 * Job-specific document access interface.
 */
export interface JobDocumentAccess {
  readonly type: 'Job'
  readonly jobId: Id
  readonly editPermission: DocumentEditPermission
}

/**
 * Unknown access type.
 */
export interface UnknownAccess {
  readonly type: 'Unknown'
}

/**
 * Union type for document access types.
 */
export type DocumentAccessType = JobDocumentAccess | UnknownAccess

/**
 * Base model for document access.
 */
export class DocumentAccess extends Model<DocumentAccess> {
  id = Id.none

  /**
   * ID of the document this access is for.
   */
  @observable accessor documentId = Id.none

  /**
   * ID of the user who granted this access.
   */
  @observable accessor grantorId = Id.none

  /**
   * Date when this access was created.
   */
  @observable accessor dateCreated = unixEpoch()

  /**
   * Date when this access was last modified.
   */
  @observable accessor dateModified = unixEpoch()

  /**
   * The type-specific access details.
   */
  @observable accessor accessType: DocumentAccessType = {
    type: 'Job',
    jobId: Id.none,
    editPermission: null,
  }

  /**
   * Sets the properties on the model.
   */
  @action.bound
  override set({ ...props }: Partial<DocumentAccessProps>): this {
    return super.set(props)
  }
}
